import type {SubscriptionStatus} from './subscriptionService';
import {
  NUMBER_OF_FREE_FIGMA_EXPORTS,
  PLUS_MONTHLY_BASE,
  PLUS_DAILY_REFILL,
  PRO_MONTHLY_BASE,
  PRO_DAILY_REFILL,
} from './constants';
import {
  isDateToday,
  getDaysDifference,
  getClosestMonthlyRenewal,
} from '@features/datetime/datetimeUtil';
import type {SubscriptionTier} from './subscriptionTier';
import {getTierDisplayName} from './subscriptionTier';
import {assertExist} from '@features/utils/assertExist';
import {
  getSessionLimits,
  resolveWindowUsage,
  SESSION_WINDOW_HOURS,
} from './sessionWindow';

/*** The "effective" subscription is the subscription is actively using at the moment.
 * At the moment, this primarily means that
 * - if you have a private subscription, you use the private subscription.
 * - if you're part of a team, you use the team's subscription.
 *
 * The goal is to provide a standard interface through which different parts of the
 * code can figure out what kind of subscription is active.
 *
 * In the future, we might extend this, and e.g. look into a `workspace` react context
 * (or pass it as argument), then the active subscription might change based on that
 */
export interface EffectiveSubscription {
  /*** individual => subscriptionsPlatform, team => teamSubscriptions */
  subscriptionSource: 'individual' | 'team';
  /** The id in the respective subscriptions table */
  subscriptionId: string;

  displayName: string;

  /** The subscription tier: free, plus, or pro */
  tier: SubscriptionTier;

  isActive: boolean;

  /**
   * Stripe status is `past_due` — the subscription is in its payment grace
   * period. Still `isActive`, but generation/export is locked (all `can*` flags
   * are forced false) until the customer pays.
   */
  isPastDue: boolean;

  /* Whether or not the user can edit the subscription (for example, can they adjust billing page) */
  isSubscriptionAdmin: boolean;

  numBonusGenerations: number | null;
  numGenerationsLeft: 'unlimited' | number;
  numGenerationsToday: number | null;
  numFigmaExportsLeft: 'unlimited' | number;
  numFigmaExportsToday: number | null;
  // Gates HTML/CSS and external-agent exports (active subscriber or free
  // allowance remaining). Figma export is paid-only and checks `isActive`.
  canExport: boolean;
  canGenerate: boolean;
  canGenerateImages: boolean;

  canFastGenerate: boolean;

  /** Credits refilled per day (paid tiers only, null for free) */
  dailyRefillAmount: number | null;
  /** Max credit balance (free) or monthly base (plus/pro) */
  maxCredits: number | 'unlimited';
  /** When the next daily refill happens (paid tiers only, tomorrow midnight) */
  nextRefillDate: Date | null;
  /** For paid tiers: when the billing cycle resets and credits fully replenish */
  billingCycleReset: Date | null;

  /**
   * Requests used in the current 5h session (paid tiers only, null for free).
   * resetsAt is null while no session is running.
   */
  sessionWindow: {used: number; limit: number; resetsAt: Date | null} | null;
}

export function getEffectiveSubscription(
  subscriptionStatus: SubscriptionStatus,
): EffectiveSubscription {
  // TODO: block app access if there is a team with inactive subscription
  // Needs:
  // - visual UI for the block.
  // - ability to leave teams
  const teamSubscription = subscriptionStatus.teams.find(
    (a) => a.subscription?.isActive,
  );

  if (teamSubscription !== undefined) {
    assertExist(
      teamSubscription.subscription,
      'teamSubscription.subscription.subscription',
    );

    const isActive = teamSubscription.subscription.isActive;
    const isPastDue = teamSubscription.subscription.pastDue;
    const tier = teamSubscription.subscription.tier;
    const billingCycleStart = teamSubscription.subscription.billingCycleStart;

    // Both Team Pro and Team Plus are credit-limited (Pro just has a higher
    // monthly base + daily refill). Counters live on the individual row.
    const generationInfo = calculatePaidCredits(
      billingCycleStart ?? new Date(),
      subscriptionStatus.individual.creditsUsed,
      subscriptionStatus.individual.creditsUseTimestamp,
      subscriptionStatus.bonusCredits,
      tier === 'pro' ? PRO_MONTHLY_BASE : PLUS_MONTHLY_BASE,
      tier === 'pro' ? PRO_DAILY_REFILL : PLUS_DAILY_REFILL,
    );

    return {
      subscriptionSource: 'team',
      subscriptionId: teamSubscription.teamId,
      isSubscriptionAdmin: teamSubscription.role === 'owner',

      displayName: getTierDisplayName(tier, true),
      tier,

      isActive,
      isPastDue,
      canExport: isActive,
      ...generationInfo,
      numFigmaExportsLeft: isActive ? 'unlimited' : 0,
      numFigmaExportsToday: null,
      canFastGenerate: tier === 'pro',
      sessionWindow: calculateSessionWindow(tier, subscriptionStatus),
      ...(isPastDue ? PAST_DUE_LOCK : {}),
    };
  }

  const indiv = subscriptionStatus.individual;
  const tier = indiv.tier ?? 'free';
  const isPastDue = indiv.pastDue;

  return {
    subscriptionSource: 'individual',
    subscriptionId: indiv.userId,
    isSubscriptionAdmin: true,
    displayName: getTierDisplayName(tier, false),
    tier,

    isActive: indiv.isActive,
    isPastDue,
    canFastGenerate: tier === 'pro',
    ...calculateScreensLeft(
      indiv,
      subscriptionStatus.bonusCredits,
      subscriptionStatus.settings,
    ),
    ...calculateFigmaExportsLeft(indiv),
    sessionWindow: calculateSessionWindow(tier, subscriptionStatus),
    ...(isPastDue ? PAST_DUE_LOCK : {}),
  };
}

/**
 * When a subscription is `past_due`, everything that would consume the
 * subscription is locked until payment succeeds. Spread last so it overrides the
 * computed `can*` flags. Credit counts are left intact so the UI still shows the
 * real balance.
 *
 * This runs server-side too: `checkGenerationAllowed` (pages/api/htmlCssGen/
 * agent.ts, the single generation endpoint for both html/css and components)
 * gates on `canGenerate`, so past-due generation is blocked at the API, not just
 * hidden in the UI. `getSubscriptionStatus` still reports past_due as active
 * (Stripe grace period) — intentional: we lock usage but keep the subscription
 * alive until Stripe resolves or cancels it. Figma export stays UI-gated only,
 * matching existing behavior (unlimited for any active subscriber).
 */
const PAST_DUE_LOCK = {
  canGenerate: false,
  canGenerateImages: false,
  canExport: false,
  canFastGenerate: false,
} as const;

/** Session counters live on the individual row, also for team members. */
function calculateSessionWindow(
  tier: SubscriptionTier,
  subscriptionStatus: SubscriptionStatus,
) {
  // Shadow mode: the server counts but never blocks — hide the UI entirely.
  if (!subscriptionStatus.settings.sessionLimitsEnforced) return null;

  const limits = getSessionLimits(tier, subscriptionStatus.settings);
  if (!limits) return null;

  const {used, resetsAt} = resolveWindowUsage(
    subscriptionStatus.individual.sessionWindowStart,
    subscriptionStatus.individual.sessionRequestCount,
    SESSION_WINDOW_HOURS,
    new Date(),
  );
  return {used, limit: limits.session, resetsAt};
}

function calculateScreensLeft(
  individualSubscription: SubscriptionStatus['individual'],
  bonusCredits: SubscriptionStatus['bonusCredits'] | undefined,
  creditSettings: SubscriptionStatus['settings'],
) {
  const tier = individualSubscription.tier;

  // Pro tier: 250/month base + 25/day refills, no cap
  if (tier === 'pro') {
    return calculatePaidCredits(
      individualSubscription.billingCycleStart ?? new Date(),
      individualSubscription.creditsUsed,
      individualSubscription.creditsUseTimestamp,
      bonusCredits ?? [],
      PRO_MONTHLY_BASE,
      PRO_DAILY_REFILL,
    );
  }

  // Plus tier: 100/month base + 10/day refills, no cap
  if (tier === 'plus') {
    return calculatePaidCredits(
      individualSubscription.billingCycleStart ?? new Date(),
      individualSubscription.creditsUsed,
      individualSubscription.creditsUseTimestamp,
      bonusCredits ?? [],
      PLUS_MONTHLY_BASE,
      PLUS_DAILY_REFILL,
    );
  }

  // Free tier: one-time signup credits, no refills
  return calculateFreeCredits(
    individualSubscription.creditsUsed,
    individualSubscription.creditsUseTimestamp,
    bonusCredits ?? [],
    creditSettings,
  );
}

function calculateFreeCredits(
  creditsUsed: number,
  creditsUseTimestamp: Date | null,
  bonusCredits: SubscriptionStatus['bonusCredits'],
  {freeMaxBalance, freeInitialCredits}: SubscriptionStatus['settings'],
) {
  const numBonusGenerations = bonusCredits.reduce(
    (acc, bc) => acc + (bc.numCredits - bc.creditsUsed),
    0,
  );
  // Never generated: grant the one-time signup credits
  if (!creditsUseTimestamp) {
    creditsUsed = freeMaxBalance - freeInitialCredits;
  }

  creditsUsed = Math.min(creditsUsed, freeMaxBalance);

  const numGenerationsLeft = numBonusGenerations + freeMaxBalance - creditsUsed;

  return {
    numGenerationsLeft,
    // total credits used — free tier has no "today" refill window
    numGenerationsToday: creditsUsed,
    canGenerate: numGenerationsLeft > 0,
    canGenerateImages: numGenerationsLeft > 0,
    dailyRefillAmount: null,
    maxCredits: freeMaxBalance,
    nextRefillDate: null,
    billingCycleReset: null,
    numBonusGenerations,
  };
}

/**
 * Calculate credits for a paid tier (Plus or Pro).
 * - `monthlyBase` credits per billing cycle
 * - +`dailyRefill` daily refill
 * - No cap on accumulation
 */
function calculatePaidCredits(
  billingCycleStart: Date,
  creditsUsed: number,
  creditsUsedTimestamp: Date | null,
  bonusCredits: SubscriptionStatus['bonusCredits'],
  monthlyBase: number,
  dailyRefill: number,
) {
  const now = new Date();

  const numBonusGenerations = bonusCredits.reduce(
    (acc, bc) => acc + (bc.numCredits - bc.creditsUsed),
    0,
  );

  // If we don't have data, fall back to max credits
  if (!creditsUsedTimestamp) {
    creditsUsed = 0;
    creditsUsedTimestamp = new Date();
  }

  creditsUsed = Math.min(creditsUsed, monthlyBase);

  // Normalize the billing period, e.g. for annual subscribers
  billingCycleStart = getClosestMonthlyRenewal(billingCycleStart);

  // If we moved to a new billing period, full credits
  if (billingCycleStart > creditsUsedTimestamp) {
    creditsUsed = 0;
    creditsUsedTimestamp = new Date();
  }
  // Days since last generation
  const daysSinceLastGen = Math.max(
    0,
    getDaysDifference(creditsUsedTimestamp, now),
  );
  const refillAmount = dailyRefill * daysSinceLastGen;

  const virtualScreenCount = Math.max(0, creditsUsed - refillAmount);
  const numGenerationsLeft =
    monthlyBase - virtualScreenCount + numBonusGenerations;

  const nextMidnightUTC = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
  );

  // Next billing cycle reset = billingCycleStart + 1 month
  const nextBillingCycleReset = new Date(billingCycleStart.getTime());
  nextBillingCycleReset.setUTCMonth(nextBillingCycleReset.getUTCMonth() + 1);

  return {
    numGenerationsLeft,
    numGenerationsToday: virtualScreenCount,
    canGenerate: numGenerationsLeft > 0,
    canGenerateImages: numGenerationsLeft > 0,
    dailyRefillAmount: dailyRefill,
    maxCredits: monthlyBase,
    nextRefillDate: nextMidnightUTC,
    billingCycleReset: nextBillingCycleReset,
    numBonusGenerations,
  };
}

function calculateFigmaExportsLeft(
  individualSubscription: SubscriptionStatus['individual'],
) {
  const lastExportTimeToday =
    individualSubscription.lastFigmaExportTime !== null &&
    isDateToday(individualSubscription.lastFigmaExportTime);

  const numFigmaExportsToday = lastExportTimeToday
    ? individualSubscription.figmaExportCount
    : 0;

  const numFigmaExportsLeft = individualSubscription.isActive
    ? ('unlimited' as const)
    : Math.max(NUMBER_OF_FREE_FIGMA_EXPORTS - numFigmaExportsToday, 0);

  const canExport =
    individualSubscription.isActive ||
    (typeof numFigmaExportsLeft === 'number' && numFigmaExportsLeft > 0);

  return {
    numFigmaExportsLeft,
    numFigmaExportsToday,
    canExport,
  };
}
